import { useCallback, useLayoutEffect, useRef } from 'react';
import type { EditorSelection } from '@/lib/djotEditing';

interface UseDjotEditorOptions {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Wires up the pure text-transform functions in `@/lib/djotEditing` (and
 * `@/lib/asciidocEditing`, despite this hook's name being format-agnostic)
 * to whichever `<textarea>` the user is actually editing in.
 *
 * An editor may render more than one editable surface bound to the same
 * `value`/`onChange` at once — e.g. `split` mode shows the raw-text
 * textarea and the `LiveEditor`'s own textarea side by side, both editing
 * the same underlying string. Toolbar actions (bold, headings, …) need to
 * apply to whichever of those the user is *actually* typing in, not a
 * single fixed element. `bindActive` should be passed to every such
 * textarea's `ref` callback (registers on mount) and `onFocus` handler
 * (promotes on focus) — the most recently bound/focused element becomes
 * `applyEdit`'s target.
 */
export function useDjotEditor({ value, onChange }: UseDjotEditorOptions) {
  const activeElRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingSelection = useRef<{ start: number; end: number } | null>(null);

  // Runs after the textarea's value has been committed to the DOM by React,
  // so `setSelectionRange` lands on the final text rather than the stale
  // pre-edit text.
  useLayoutEffect(() => {
    const pending = pendingSelection.current;
    const el = activeElRef.current;
    if (pending && el) {
      el.focus();
      el.setSelectionRange(pending.start, pending.end);
      pendingSelection.current = null;
    }
  }, [value]);

  const applyEdit = useCallback(
    (transform: (sel: EditorSelection) => EditorSelection) => {
      const el = activeElRef.current;
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? value.length;
      const result = transform({ value, start, end });
      pendingSelection.current = { start: result.start, end: result.end };
      onChange(result.value);
    },
    [value, onChange],
  );

  const bindActive = useCallback((el: HTMLTextAreaElement | null) => {
    if (el) activeElRef.current = el;
  }, []);

  return { activeElRef, bindActive, applyEdit };
}
